import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtMoney, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { GitMerge, CheckCircle2 } from "lucide-react";

function Side({ c, highlight }) {
  const addr = c.address || {};
  return (
    <div className={`rounded-md border p-5 ${highlight ? "border-primary/40 bg-secondary/40" : "border-border"}`}>
      <p className="font-serif text-xl font-semibold">{c.first_name} {c.last_name}</p>
      <dl className="mt-3 space-y-1.5 text-sm">
        <div className="flex gap-2"><dt className="text-muted-foreground w-16">Email</dt><dd>{c.email || "—"}</dd></div>
        <div className="flex gap-2"><dt className="text-muted-foreground w-16">Phone</dt><dd>{c.phone || "—"}</dd></div>
        <div className="flex gap-2"><dt className="text-muted-foreground w-16">Address</dt><dd>{[addr.street, addr.city, addr.state, addr.zip].filter(Boolean).join(", ") || "—"}</dd></div>
        <div className="flex gap-2"><dt className="text-muted-foreground w-16">Orders</dt><dd>{c.order_count} · {fmtMoney(c.lifetime_spend)} lifetime</dd></div>
        <div className="flex gap-2"><dt className="text-muted-foreground w-16">Since</dt><dd>{fmtDate(c.first_seen)}</dd></div>
      </dl>
    </div>
  );
}

export default function DuplicatesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [pairs, setPairs] = useState(null);

  const load = useCallback(() => {
    api.get("/duplicates").then((r) => setPairs(r.data)).catch((e) => toast.error(apiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const merge = async (pairId, keepId) => {
    try {
      await api.post(`/duplicates/${pairId}/merge`, { keep_id: keepId });
      toast.success("Records merged. The order history is now combined.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const dismiss = async (pairId) => {
    try {
      await api.post(`/duplicates/${pairId}/dismiss`);
      toast.success("Marked as different people. This pair won't come up again.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8" data-testid="duplicates-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-serif font-semibold">Review Duplicates</h1>
        <p className="text-muted-foreground mt-2">
          These pairs look like they might be the same person. Merging combines their order history into one record.
        </p>
      </div>

      {pairs === null ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : pairs.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="p-12 text-center">
            <CheckCircle2 className="h-10 w-10 text-primary mx-auto" />
            <p className="font-serif text-2xl font-semibold mt-4">Nothing to review</p>
            <p className="text-muted-foreground text-sm mt-1" data-testid="no-duplicates-message">Every customer record is looking tidy.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {pairs.map(({ pair, customer_a, customer_b }) => (
            <Card key={pair.id} className="shadow-sm" data-testid={`duplicate-pair-${pair.id}`}>
              <CardContent className="p-6 space-y-5">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <GitMerge className="h-4 w-4 text-primary" /> Why we flagged this: {pair.reason}. Found on {fmtDate(pair.created_at)}.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <Side c={customer_a} />
                    {isAdmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="secondary" className="w-full" data-testid={`keep-a-btn-${pair.id}`}>Keep this record</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif">Merge into {customer_a.first_name} {customer_a.last_name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {customer_b.first_name} {customer_b.last_name}'s orders will move into this record, and their record will be removed. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Go back</AlertDialogCancel>
                            <AlertDialogAction onClick={() => merge(pair.id, customer_a.id)} data-testid={`confirm-keep-a-${pair.id}`}>Yes, merge them</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                  <div className="space-y-3">
                    <Side c={customer_b} highlight />
                    {isAdmin && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="secondary" className="w-full" data-testid={`keep-b-btn-${pair.id}`}>Keep this record</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-serif">Merge into {customer_b.first_name} {customer_b.last_name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {customer_a.first_name} {customer_a.last_name}'s orders will move into this record, and their record will be removed. This cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Go back</AlertDialogCancel>
                            <AlertDialogAction onClick={() => merge(pair.id, customer_b.id)} data-testid={`confirm-keep-b-${pair.id}`}>Yes, merge them</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
                {isAdmin && (
                  <div className="text-center">
                    <Button variant="ghost" onClick={() => dismiss(pair.id)} data-testid={`dismiss-btn-${pair.id}`}>
                      These are different people — keep both
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
